import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";

const safeMsg = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

const toDateInput = (v) => {
  if (!v) return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : "";
};

const emptyExperience = () => ({ company: "", position: "", start_date: "", end_date: "", description: "" });
const emptyEducation = () => ({ institution: "", degree: "", field: "", graduation_date: "" });
const emptyLanguage = () => ({ language: "", proficiency: "" });
const emptyCertificate = () => ({ title: "", file_path: "" });

const FIELD_GRID = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
  gap: 2,
};
const FULL = { gridColumn: "1 / -1" };

/* --------------------------- shared section card --------------------------- */
function SectionCard({ icon, title, subtitle, action, children }) {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, overflow: "hidden", bgcolor: theme.palette.background.paper }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
            theme.palette.info.main,
            0.05
          )} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 36,
            height: 36,
            bgcolor: alpha(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>{children}</Box>
    </Paper>
  );
}

export default function PublicCandidateCvPage() {
  const theme = useTheme();
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [link, setLink] = useState(null);
  const [candidate, setCandidate] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    relocate: false,
    expected_salary: "",
    current_salary: "",
    joining_availability_date: "",
    current_job_title: "",
    notes: "",
    cv_submitted_at: null,
  });
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [languageSkills, setLanguageSkills] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const locked = useMemo(() => Boolean(candidate?.cv_submitted_at), [candidate?.cv_submitted_at]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const res = await api.get(`/public/candidate-cv/${token}`);
      const out = res?.data?.data ?? {};
      const c = out?.candidate ?? {};
      setLink(out?.link ?? null);
      setCandidate({
        full_name: c.full_name ?? "",
        email: c.email ?? "",
        phone: c.phone ?? "",
        address: c.address ?? "",
        relocate: !!c.relocate,
        expected_salary: c.expected_salary ?? "",
        current_salary: c.current_salary ?? "",
        joining_availability_date: toDateInput(c.joining_availability_date),
        current_job_title: c.current_job_title ?? "",
        notes: c.notes ?? "",
        cv_submitted_at: c.cv_submitted_at ?? null,
      });
      setExperiences(
        (out?.experiences ?? []).map((x) => ({
          id: x.id,
          company: x.company ?? "",
          position: x.position ?? "",
          start_date: toDateInput(x.start_date),
          end_date: toDateInput(x.end_date),
          description: x.description ?? "",
        }))
      );
      setEducations(
        (out?.educations ?? []).map((x) => ({
          id: x.id,
          institution: x.institution ?? "",
          degree: x.degree ?? "",
          field: x.field ?? "",
          graduation_date: toDateInput(x.graduation_date),
        }))
      );
      setLanguageSkills(
        (out?.language_skills ?? []).map((x) => ({
          id: x.id,
          language: x.language ?? "",
          proficiency: x.proficiency ?? "",
        }))
      );
      setCertificates(
        (out?.certificates ?? []).map((x) => ({
          id: x.id,
          title: x.title ?? "",
          file_path: x.file_path ?? "",
        }))
      );
    } catch (e) {
      setError(safeMsg(e, "Unable to load this candidate CV link."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const saveAll = async () => {
    if (locked) return false;
    setSaving(true);
    setError("");
    setInfo("");
    try {
      const payload = {
        ...candidate,
        experiences,
        educations,
        language_skills: languageSkills,
        certificates,
      };
      const res = await api.put(`/public/candidate-cv/${token}`, payload);
      const out = res?.data?.data ?? {};
      const c = out?.candidate ?? {};
      setCandidate((prev) => ({ ...prev, cv_submitted_at: c.cv_submitted_at ?? prev.cv_submitted_at }));
      setInfo("Saved successfully.");
      return true;
    } catch (e) {
      setError(safeMsg(e, "Failed to save."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submitAll = async () => {
    if (locked) return;
    if (!window.confirm("Submit now? After submission, this form will be locked.")) return;
    setSubmitting(true);
    setError("");
    setInfo("");
    try {
      const saved = await saveAll();
      if (!saved) return;
      const res = await api.post(`/public/candidate-cv/${token}/submit`);
      const submittedAt = res?.data?.data?.cv_submitted_at || new Date().toISOString();
      setCandidate((prev) => ({ ...prev, cv_submitted_at: submittedAt }));
      setInfo("Submitted successfully. Thank you!");
    } catch (e) {
      setError(safeMsg(e, "Failed to submit."));
    } finally {
      setSubmitting(false);
    }
  };

  const generatePdf = async () => {
    setGenerating(true);
    setError("");
    setInfo("");
    try {
      const res = await api.get(`/public/candidate-cv/${token}/generate`, {
        params: { download: 1 },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(safeMsg(e, "Failed to generate CV."));
    } finally {
      setGenerating(false);
    }
  };

  const updateArrayRow = (setter, idx, key, value) =>
    setter((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  const removeArrayRow = (setter, idx) => setter((prev) => prev.filter((_, i) => i !== idx));

  const setField = (key) => (e) => setCandidate((p) => ({ ...p, [key]: e.target.value }));

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const pageBg =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.default, 1)
      : alpha(theme.palette.primary.main, 0.04);

  const heroBg = `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(
    theme.palette.info.main,
    0.9
  )} 100%)`;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: pageBg, pt: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        {/* Hero */}
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", mb: 3 }}>
          <Box sx={{ p: { xs: 2.5, md: 4 }, background: heroBg, color: "#fff" }}>
            <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.85, fontWeight: 700 }}>
              Curriculum Vitae
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  {candidate.full_name ? candidate.full_name : "Complete your CV"}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                  {locked
                    ? "Your CV has been submitted."
                    : "Fill in your details below, then save or submit your CV."}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={locked ? <TaskAltRoundedIcon sx={{ color: "#fff !important" }} /> : undefined}
                  label={locked ? "Submitted" : "Draft"}
                  sx={{
                    fontWeight: 800,
                    color: "#fff",
                    bgcolor: alpha("#fff", locked ? 0.28 : 0.18),
                    border: `1px solid ${alpha("#fff", 0.5)}`,
                  }}
                />
                {link?.expires_at ? (
                  <Chip
                    icon={<AccessTimeRoundedIcon sx={{ color: "#fff !important" }} />}
                    label={`Expires ${new Date(link.expires_at).toLocaleDateString()}`}
                    sx={{ fontWeight: 700, color: "#fff", bgcolor: alpha("#fff", 0.14), border: `1px solid ${alpha("#fff", 0.4)}` }}
                  />
                ) : null}
              </Stack>
            </Stack>
          </Box>

          {(error || info || locked) && (
            <Box sx={{ p: { xs: 1.5, md: 2 }, pt: 2 }}>
              {error ? <Alert severity="error" sx={{ mb: info || locked ? 1.5 : 0 }}>{error}</Alert> : null}
              {info ? <Alert severity="success" sx={{ mb: locked ? 1.5 : 0 }}>{info}</Alert> : null}
              {locked ? (
                <Alert severity="info">This CV is already submitted and can no longer be edited from this link.</Alert>
              ) : null}
            </Box>
          )}
        </Paper>

        <Stack spacing={3} sx={{ pb: 12 }}>
          {/* Basic info */}
          <SectionCard icon={<PersonOutlineRoundedIcon />} title="Basic information" subtitle="Your contact and role details">
            <Box sx={FIELD_GRID}>
              <TextField label="Full name" value={candidate.full_name} onChange={setField("full_name")} fullWidth disabled={locked} />
              <TextField label="Email" value={candidate.email} onChange={setField("email")} fullWidth disabled={locked} />
              <TextField label="Phone" value={candidate.phone} onChange={setField("phone")} fullWidth disabled={locked} />
              <TextField label="Current job title" value={candidate.current_job_title} onChange={setField("current_job_title")} fullWidth disabled={locked} />
              <Box sx={FULL}>
                <TextField label="Address" value={candidate.address} onChange={setField("address")} fullWidth disabled={locked} />
              </Box>
              <TextField label="Expected salary" value={candidate.expected_salary} onChange={setField("expected_salary")} fullWidth disabled={locked} />
              <TextField label="Current salary" value={candidate.current_salary} onChange={setField("current_salary")} fullWidth disabled={locked} />
              <TextField
                label="Joining availability date"
                type="date"
                value={candidate.joining_availability_date || ""}
                onChange={setField("joining_availability_date")}
                fullWidth
                disabled={locked}
                InputLabelProps={{ shrink: true }}
              />
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  width: "fit-content",
                  pl: 1.5,
                  pr: 2,
                  borderRadius: 999,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={Boolean(candidate.relocate)}
                      onChange={(e) => setCandidate((p) => ({ ...p, relocate: e.target.checked }))}
                      disabled={locked}
                    />
                  }
                  label="Willing to relocate"
                />
              </Box>
              <Box sx={FULL}>
                <TextField
                  label="Professional summary / notes"
                  value={candidate.notes}
                  onChange={setField("notes")}
                  fullWidth
                  multiline
                  minRows={4}
                  disabled={locked}
                />
              </Box>
            </Box>
          </SectionCard>

          <ListSection
            title="Experience"
            icon={<WorkOutlineRoundedIcon />}
            rows={experiences}
            setRows={setExperiences}
            disabled={locked}
            emptyRow={emptyExperience}
            fields={[
              { key: "position", label: "Position" },
              { key: "company", label: "Company" },
              { key: "start_date", label: "Start date", type: "date" },
              { key: "end_date", label: "End date", type: "date" },
              { key: "description", label: "Description", multiline: true, rows: 3, full: true },
            ]}
            updateArrayRow={updateArrayRow}
            removeArrayRow={removeArrayRow}
          />

          <ListSection
            title="Education"
            icon={<SchoolOutlinedIcon />}
            rows={educations}
            setRows={setEducations}
            disabled={locked}
            emptyRow={emptyEducation}
            fields={[
              { key: "institution", label: "Institution" },
              { key: "degree", label: "Degree" },
              { key: "field", label: "Field" },
              { key: "graduation_date", label: "Graduation date", type: "date" },
            ]}
            updateArrayRow={updateArrayRow}
            removeArrayRow={removeArrayRow}
          />

          <ListSection
            title="Language skills"
            icon={<TranslateRoundedIcon />}
            rows={languageSkills}
            setRows={setLanguageSkills}
            disabled={locked}
            emptyRow={emptyLanguage}
            fields={[
              { key: "language", label: "Language" },
              { key: "proficiency", label: "Proficiency" },
            ]}
            updateArrayRow={updateArrayRow}
            removeArrayRow={removeArrayRow}
          />

          <ListSection
            title="Certificates"
            icon={<WorkspacePremiumOutlinedIcon />}
            rows={certificates}
            setRows={setCertificates}
            disabled={locked}
            emptyRow={emptyCertificate}
            fields={[
              { key: "title", label: "Title" },
              { key: "file_path", label: "File path / URL" },
            ]}
            updateArrayRow={updateArrayRow}
            removeArrayRow={removeArrayRow}
          />
        </Stack>
      </Container>

      {/* Sticky action bar */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(8px)",
        }}
      >
        <Container maxWidth="lg" sx={{ py: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={generating ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
              onClick={generatePdf}
              disabled={generating}
              sx={{ borderRadius: 2.5, fontWeight: 800 }}
            >
              {generating ? "Generating…" : "Generate PDF"}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
              onClick={saveAll}
              disabled={locked || saving}
              sx={{ borderRadius: 2.5, fontWeight: 800 }}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleRoundedIcon />}
              onClick={submitAll}
              disabled={locked || submitting}
              sx={{ borderRadius: 2.5, fontWeight: 800 }}
            >
              {submitting ? "Submitting…" : "Submit CV"}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

function ListSection({ title, icon, rows, setRows, disabled, emptyRow, fields, updateArrayRow, removeArrayRow }) {
  const theme = useTheme();
  return (
    <SectionCard
      icon={icon}
      title={title}
      subtitle={`${rows.length} ${rows.length === 1 ? "entry" : "entries"}`}
      action={
        !disabled ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={() => setRows((prev) => [...prev, emptyRow()])}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            Add
          </Button>
        ) : null
      }
    >
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No records yet.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {rows.map((row, idx) => (
            <Paper
              key={row?.id ?? `${title}-${idx}`}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2.5, bgcolor: alpha(theme.palette.background.default, 0.5) }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary" }}>
                  #{idx + 1}
                </Typography>
                {!disabled ? (
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => removeArrayRow(setRows, idx)}
                      sx={{
                        color: theme.palette.error.main,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.16) },
                      }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
              <Box sx={FIELD_GRID}>
                {fields.map((f) => {
                  const cell = (
                    <TextField
                      label={f.label}
                      value={row?.[f.key] ?? ""}
                      onChange={(e) => updateArrayRow(setRows, idx, f.key, e.target.value)}
                      type={f.type || "text"}
                      fullWidth
                      multiline={!!f.multiline}
                      minRows={f.multiline ? f.rows || 3 : undefined}
                      disabled={disabled}
                      InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                    />
                  );
                  return f.full ? (
                    <Box key={f.key} sx={FULL}>
                      {cell}
                    </Box>
                  ) : (
                    <Box key={f.key}>{cell}</Box>
                  );
                })}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}
